export type InvestorCohortOption = {
  id: number;
  name: string;
  slug: string;
  status: string;
  membershipStatus: string | null;
};

async function responseOrThrow(response: Response, action: string) {
  if (response.ok) return response;
  const body = await response.json().catch(() => null);
  throw new Error(`${action}: ${typeof body?.error === "string" ? body.error : `HTTP ${response.status}`}`);
}

export async function loadInvestorMemberships(
  base: string,
  investorId: number,
  headers: Record<string, string>,
  request: typeof fetch = fetch,
): Promise<InvestorCohortOption[]> {
  const response = await responseOrThrow(
    await request(`${base}api/admin/investors/${investorId}/cohort-memberships`, { headers }),
    "Load cohort memberships",
  );
  const body = await response.json();
  if (!Array.isArray(body?.cohorts) || !body.cohorts.every((cohort: InvestorCohortOption) =>
    Number.isSafeInteger(cohort.id) && typeof cohort.name === "string" && typeof cohort.slug === "string"
    && typeof cohort.status === "string" && (cohort.membershipStatus === null || typeof cohort.membershipStatus === "string")
  )) throw new Error("Load cohort memberships: invalid response");
  return body.cohorts;
}

export async function saveInvestorMemberships(
  base: string,
  investorId: number,
  initial: number[],
  selected: number[],
  headers: Record<string, string>,
  request: typeof fetch = fetch,
) {
  const oldIds = new Set(initial);
  const newIds = new Set(selected);
  const changes = [
    ...selected.filter(id => !oldIds.has(id)).map(id => ({ id, method: "POST" })),
    ...initial.filter(id => !newIds.has(id)).map(id => ({ id, method: "DELETE" })),
  ];
  const failures: string[] = [];
  let saved = 0;
  for (const { id, method } of changes) {
    try {
      await responseOrThrow(await request(
        `${base}api/cohorts/${id}/investors${method === "DELETE" ? `/${investorId}` : ""}`,
        { method, headers, ...(method === "POST" ? { body: JSON.stringify({ investorId }) } : {}) },
      ), `${method === "POST" ? "Add to" : "Remove from"} cohort ${id}`);
      saved++;
    } catch (error) {
      failures.push(error instanceof Error ? error.message : `Cohort ${id}: network error`);
    }
  }
  return { saved, failures };
}